import {
  PhysicalPersonProspect,
  LegalEntityProspect,
  CreateProspectRequest,
} from "../../requests/entities/Prospect";

export interface ApiIdPaper {
  type: string;
  idPaperNumber: string;
  deliveryDate: string; // ISO date string
  validityDate: string; // ISO date string
  deliveryPlace?: string;
  organisationWhichDeliver?: string;
}

export interface ApiGeoLocation {
  latitude: number;
  longitude: number;
}

export interface ApiAddress {
  type: string;
  street: string;
  area?: string;
  city: string;
  localGovernment?: string;
  country: string;
  geoLocation?: ApiGeoLocation;
}

/**
 * Bloc entreprise — miroir de LegalEntityBlock cote back. Requis uniquement quand
 * `segment = "PM"`, ignore (et non valide par le back) pour un prospect PP.
 */
export interface ApiLegalEntityBlock {
  companyName: string;
  legalFormCode: string;
  incorporationCountryCode: string;
  rccm: string;
  nif?: string;
  sectorActivityCode?: string;
  companyPhone: string;
  companyCountryCode: string;
  companyEmail?: string;
  companyPhoneChallengeId?: string;
  companyEmailChallengeId?: string;
  declarantFullName: string;
  declarantRole: string;
  declarantPhone: string;
  declarantPhoneChallengeId?: string;
}

export interface ApiProspectRequest {
  // Common fields
  segment?: string;
  canal?: string;
  paysId?: number;

  // PP fields
  businessKey?: string;
  full_name?: string;
  // first_name?: string;
  // last_name?: string;
  // gender?: string;
  email?: string;
  countrycode?: string;
  phone?: string;
  // nationalityId?: number;
  // birthDate?: string; // ISO date string
  // civilite?: string;
  // city?: string;
  product?: string;
  comment?: string;
  accountPurposeCode?: string;
  contactForSavingCardCredit?: boolean;

  /**
   * Challenges OTP deja verifies. Le back ne se fie pas aux drapeaux du formulaire : il revalide
   * le challenge (verifie, recent, bon canal, meme destination) avant de marquer le contact
   * verifie. Absents ou non concordants -> contact enregistre comme non verifie, sans echec.
   */
  phoneChallengeId?: string;
  emailChallengeId?: string;

  birthPlace?: string;
  idPaper?: ApiIdPaper;
  address?: ApiAddress;

  // PM : bloc entreprise, requis si segment = PM (cf. ApiLegalEntityBlock).
  legalEntity?: ApiLegalEntityBlock;
}

export type ApiCreateProspectRequest = ApiProspectRequest;

// Shape returned by the API after creation
export interface ApiCreatedProspect {
  business_key: string;
  full_name: string;
  // first_name: string;
  // last_name: string;
  contact?: unknown | null;
  pays?: unknown | null;
  canal: string | null;
  screening?: unknown | null;
  status: string; // e.g. "PROSPECT"
  type: "Personne physique" | "Personne morale";
  last_update?: string | null;
}

export interface ApiCreateProspectResponse {
  success: boolean;
  data?: ApiCreatedProspect;
  error?: string;
}

export default class ProspectDataMapper {
  static toApiRequest(
    request: CreateProspectRequest
  ): ApiCreateProspectRequest {
    const { prospect } = request;

    const apiProspect: ApiProspectRequest = {
      canal: prospect.channel,
      // Sans ce champ, le back defaute a PP et rejette le bloc legalEntity d'un
      // prospect PM (LegalEntityRequiredForPm) - trouve en rejouant la soumission
      // reelle jusqu'au bout, jamais couvert par les tests unitaires du mapper.
      segment: prospect.segment,
    };

    if (prospect.segment === "PP") {
      const ppProspect = prospect as PhysicalPersonProspect;

      // Basic PP fields
      // apiProspect.businessKey = ppProspect.businessKey;
      apiProspect.full_name = ppProspect.fullName;
      // apiProspect.civilite = ppProspect.civilite;
      // apiProspect.first_name = ppProspect.firstName;
      // apiProspect.last_name = ppProspect.lastName;
      // apiProspect.gender = ppProspect.sex;
      apiProspect.email = ppProspect.email;
      apiProspect.countrycode = ppProspect.countrycode;
      apiProspect.phone = ppProspect.mobile;
      // apiProspect.nationalityId = parseInt(ppProspect.nationality); // Convert nationality string to number
      // apiProspect.birthDate = ppProspect.birthDate?.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      // apiProspect.city = ppProspect.city;

      // Intention/Interest fields
      apiProspect.product = ppProspect.productOfInterest;
      apiProspect.accountPurposeCode = ppProspect.accountPurposeCode;
      apiProspect.comment = ppProspect.comment;
      apiProspect.contactForSavingCardCredit = ppProspect.wantsContact;

      // Preuves de verification des contacts (cf. ApiProspectRequest)
      apiProspect.phoneChallengeId = ppProspect.mobileChallengeId ?? undefined;
      apiProspect.emailChallengeId = ppProspect.emailChallengeId ?? undefined;

      // // ID Paper object
      // if (ppProspect.idType || ppProspect.idNumber) {
      //   apiProspect.idPaper = {
      //     type: ppProspect.idType || "",
      //     idPaperNumber: ppProspect.idNumber || "",
      //     deliveryDate:
      //       ppProspect.idIssueDate?.toISOString().split("T")[0] || "",
      //     validityDate:
      //       ppProspect.idExpiryDate?.toISOString().split("T")[0] || "",
      //     deliveryPlace: "", // Not provided in current form
      //     organisationWhichDeliver: "", // Not provided in current form
      //   };
      // }

      // Address object
      // if (ppProspect.addressLine1 || ppProspect.city) {
      //   apiProspect.address = {
      //     type: "HOME", // Default type
      //     street: ppProspect.addressLine1 || "",
      //     area: ppProspect.addressLine2 || "",
      //     city: ppProspect.city || "",
      //     localGovernment: "", // Not provided in current form
      //     country: prospect.country,
      //     geoLocation: {
      //       latitude: 0, // Default values
      //       longitude: 0,
      //     },
      //   };
      // }
    } else if (prospect.segment === "PM") {
      const pmProspect = prospect as LegalEntityProspect;
      apiProspect.businessKey = pmProspect.businessKey;

      // Champs racine = identite du DECLARANT, meme convention que le prospect PP
      // (full_name/phone/countrycode obligatoires cote back quel que soit le segment).
      apiProspect.full_name = pmProspect.contactPerson;
      apiProspect.phone = pmProspect.contactPersonPhone;
      // Indicatif telephonique du declarant (ex. "+225") — PAS prospect.country, qui est le
      // contexte metier de la demande ("CIV"). Bug trouve en rejouant la soumission reelle :
      // le back recevait "CIV" dans countrycode et l'acceptait quand meme (NotBlank seulement),
      // mais ce n'est pas la valeur voulue.
      apiProspect.countrycode = pmProspect.contactPersonCountryCode;
      apiProspect.email = pmProspect.contactPersonEmail;

      apiProspect.legalEntity = {
        companyName: pmProspect.companyName,
        legalFormCode: pmProspect.legalFormCode,
        incorporationCountryCode: pmProspect.incorporationCountryCode,
        rccm: pmProspect.registrationNumber,
        nif: pmProspect.taxId,
        sectorActivityCode: pmProspect.sectorActivityCode,
        companyPhone: pmProspect.companyPhone,
        companyCountryCode: pmProspect.companyCountryCode,
        companyEmail: pmProspect.companyEmail,
        companyPhoneChallengeId: pmProspect.companyPhoneChallengeId ?? undefined,
        companyEmailChallengeId: pmProspect.companyEmailChallengeId ?? undefined,
        declarantFullName: pmProspect.contactPerson,
        declarantRole: pmProspect.declarantRole,
        declarantPhone: pmProspect.contactPersonPhone,
        declarantPhoneChallengeId:
          pmProspect.contactPersonMobileChallengeId ?? undefined,
      };

      // Intention/Interest fields — same shape as the PP branch above.
      apiProspect.product = pmProspect.productOfInterest;
      apiProspect.accountPurposeCode = pmProspect.accountPurposeCode;
      apiProspect.comment = pmProspect.comment;
      apiProspect.contactForSavingCardCredit = pmProspect.wantsContact;

      // Preuves de verification des contacts du declarant (cf. ApiProspectRequest) —
      // memes challenges que ceux portes dans legalEntity.declarantPhoneChallengeId.
      apiProspect.phoneChallengeId =
        pmProspect.contactPersonMobileChallengeId ?? undefined;
      apiProspect.emailChallengeId =
        pmProspect.contactPersonEmailChallengeId ?? undefined;
    }

    return apiProspect;
  }

  static fromApiResponse(
    response: ApiCreateProspectResponse
  ): CreateProspectRequest {
    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to create prospect");
    }

    const created = response.data; // ApiCreatedProspect

    const isPP = created.type === "Personne physique" || "PERSONNE_PHYSIQUE";

    // The API response after creation is minimal, so we map what we have
    const baseProspect = {
      segment: isPP ? "PP" : "PM",
      channel: created.canal ?? "",
      country: created.pays?.toString() ?? "", // Not provided in the response
    };

    if (isPP) {
      const prospect: PhysicalPersonProspect = {
        ...baseProspect,
        businessKey: created.business_key,
        segment: "PP",
        fullName: created.full_name || "",
        // civilite: undefined,
        // firstName: created.first_name || "",
        // lastName: created.last_name || "",
        // sex: "", // Not provided
        // nationality: "", // Not provided
        countrycode: "", // Not provided
        mobile: "", // Not provided
        // email: undefined,
        // birthDate: undefined,
        // city: undefined,
        productOfInterest: undefined,
        accountPurposeCode: undefined,
        wantsContact: undefined,
      };
      return { prospect };
    }

    const prospect: LegalEntityProspect = {
      ...baseProspect,
      type: "PM",
      companyName: "", // Not provided
      legalFormCode: "",
      incorporationCountryCode: "",
      registrationNumber: "",
      taxId: undefined,
      companyPhone: "",
      companyCountryCode: "",
      companyEmail: undefined,
      contactPerson: "",
      declarantRole: "",
      contactPersonEmail: "",
      contactPersonPhone: "",
      contactPersonCountryCode: "",
    };
    return { prospect };
  }
}
